/**
 * Plant relay load schedule report grid — relay load zones/panels with their illumination
 * schedules for a site group.
 *
 * Server-backed via EntityGrid over odl-app/relay-loads-schedule-report/. Columns —
 * including the dynamic schedule_1…schedule_N columns — come from the backend grid-spec
 * descriptor, so no schedule count needs to be fetched client-side. The site group scope
 * is enforced via extraParams. The relay-load column header and count noun follow the
 * scope customer's CustomerType term; the site/structure header stays on the billboard
 * binary. Excel export calls the legacy /relayloads/schedule_report_export/ endpoint and
 * downloads the blob directly.
 *
 * Contains:
 * - PlantRelayLoadsGrid — EntityGrid over odl-app/relay-loads-schedule-report/ (default export)
 */

import { FileDownload } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import FileSaver from 'file-saver';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fetchRelayLoadsScheduleReportExcel } from '@sophocles/api/reports';
import { EntityGrid } from '@sophocles/data-grids';
import { ToolbarIconButton } from '@sophocles/data-grids/components/toolbar';
import useRelayLoadTerm from '@sophocles/hooks/useRelayLoadTerm';

const SITE_GROUP_FILTER = 'primary_relay__relay_controller__site__site_groups';

function PlantRelayLoadsGrid({ siteGroupId, isBillboard }) {
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const { singular: relayLoadTerm, plural: relayLoadTermPlural } = useRelayLoadTerm(
    isBillboard
      ? { singularKey: 'reports.panel', pluralKey: 'reports.panels' }
      : { singularKey: 'reports.zone', pluralKey: 'reports.zones' },
  );

  // extraParams drives the EntityGrid list fetch via filter[site_group_id].
  const extraParams = useMemo(() => ({ site_group_id: siteGroupId }), [siteGroupId]);

  // Excel export: legacy blob endpoint — the file is generated server-side.
  const handleExport = useCallback(() => {
    setExporting(true);
    fetchRelayLoadsScheduleReportExcel(SITE_GROUP_FILTER, siteGroupId)
      .then(blob => {
        const filename = `${relayLoadTerm.toLowerCase()}_illumination_report_${moment().format('YYYY_MM_DD_mmss')}.xlsx`;
        FileSaver.saveAs(blob, filename);
      })
      .finally(() => setExporting(false));
  }, [siteGroupId, relayLoadTerm]);

  // Header-name overrides: relay-load term is customer-type-driven; the
  // structure-vs-site header stays on the billboard/commercial binary.
  // Sizing, order, and other column properties come from the descriptor.
  const fieldOverrides = useMemo(
    () => ({
      name: { headerName: relayLoadTerm },
      site_name: {
        headerName: isBillboard ? t('reports.structure', { defaultValue: 'Structure' }) : t('reports.site'),
      },
    }),
    [isBillboard, relayLoadTerm, t],
  );

  const exportButton = (
    <ToolbarIconButton
      tooltip={t('general.export', { defaultValue: 'Export' })}
      onClick={handleExport}
      disabled={exporting}
    >
      {exporting ? <CircularProgress size={18} /> : <FileDownload sx={{ fontSize: 18 }} />}
    </ToolbarIconButton>
  );

  return (
    <EntityGrid
      basePath="relay-loads-schedule-report"
      fieldOverrides={fieldOverrides}
      extraParams={extraParams}
      options={{
        persistKey: 'plantRelayLoadsGrid',
        toolbarActions: exportButton,
        toolbarVariant: 'flat',
        countNoun: relayLoadTermPlural,
        gridProps: {
          density: 'compact',
          disableRowSelectionOnClick: true,
        },
      }}
    />
  );
}

export default PlantRelayLoadsGrid;
